import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { collection, getDocs, addDoc, query, where, orderBy, updateDoc, doc, arrayUnion, arrayRemove, setDoc } from 'firebase/firestore';
import { User } from 'firebase/auth';
import PageHeader from './ui/PageHeader';
import SectionCard from './ui/SectionCard';
import TabNav from './ui/TabNav';
import { containerClass } from '../config/theme';
import MockupMaker from './MockupMaker';

interface TrainingModule {
  id: string;
  title: string;
  category: 'podcast' | 'presentation' | 'networking' | 'leadership' | 'technical';
  description: string;
  duration: number; // minutes
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  instructor: string;
  instructorPhoto?: string;
  thumbnail?: string;
  videoUrl?: string;
  materials: TrainingMaterial[];
  exercises: TrainingExercise[];
  prerequisites: string[];
  learningObjectives: string[];
  isPublished: boolean;
  createdAt: Date;
  updatedAt: Date;
  enrolledCount: number;
  completionRate: number;
  rating: number;
  tags: string[];
}

interface TrainingMaterial {
  id: string;
  title: string;
  type: 'pdf' | 'video' | 'audio' | 'link' | 'template';
  url: string;
  description: string;
  isRequired: boolean;
}

interface TrainingExercise {
  id: string;
  title: string;
  description: string;
  type: 'practice' | 'assignment' | 'quiz' | 'roleplay';
  duration: number; // minutes
  instructions: string;
  expectedOutcome: string;
}

interface UserProgress {
  id: string;
  userId: string;
  moduleId: string;
  moduleTitle: string;
  progress: number; // 0-100
  completedSections: string[];
  currentSection: string;
  startedAt: Date;
  completedAt?: Date;
  exercisesCompleted: string[];
  certificateEarned: boolean;
  certificateUrl?: string;
  notes: string;
  rating?: number;
  feedback?: string;
}

interface TrainingSession {
  id: string;
  moduleId: string;
  title: string;
  instructor: string;
  date: Date;
  duration: number;
  maxParticipants: number;
  currentParticipants: number;
  location: 'online' | 'offline' | 'hybrid';
  meetingUrl?: string;
  address?: string;
  description: string;
  isActive: boolean;
  participants: string[];
}

interface TrainingCenterProps {
  isAdmin?: boolean;
}

const TrainingCenter: React.FC<TrainingCenterProps> = ({ isAdmin = false }) => {
  const [user, setUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<'modules' | 'sessions' | 'progress' | 'create' | 'mockups'>('modules');
  const [trainingModules, setTrainingModules] = useState<TrainingModule[]>([]);
  const [userProgress, setUserProgress] = useState<UserProgress[]>([]);
  const [trainingSessions, setTrainingSessions] = useState<TrainingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState<TrainingModule | null>(null);
  const [showCreateModule, setShowCreateModule] = useState(false);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Form states
  const [newModule, setNewModule] = useState({
    title: '',
    category: 'podcast' as TrainingModule['category'],
    description: '',
    duration: 60,
    difficulty: 'beginner' as TrainingModule['difficulty'],
    instructor: '',
    prerequisites: [] as string[],
    learningObjectives: [] as string[],
    tags: [] as string[]
  });

  const [newSession, setNewSession] = useState({
    moduleId: '',
    title: '',
    instructor: '',
    date: new Date(),
    duration: 120,
    maxParticipants: 20,
    location: 'online' as 'online' | 'offline' | 'hybrid',
    description: ''
  });

  useEffect(() => {
    checkAdminStatus();
    loadData();
  }, []);

  const checkAdminStatus = async () => {
    const adminEmails = ['info@superkonnected.nl', 'support@superkonnected.nl', 'bussinesssecurit@gmail.com'];
    if (user && adminEmails.includes(user.email || '')) {
      // Admin status is handled by parent component
      console.log('Admin user detected:', user.email);
    }
  };

  const loadData = async () => {
    try {
      // Load training modules
      const modulesQuery = query(collection(db, 'trainingModules'), orderBy('createdAt', 'desc'));
      const modulesSnapshot = await getDocs(modulesQuery);
      const modulesData = modulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt.toDate()
      })) as TrainingModule[];
      setTrainingModules(modulesData);

      // Load user progress
      if (user) {
        const progressQuery = query(collection(db, 'userProgress'), where('userId', '==', user.uid));
        const progressSnapshot = await getDocs(progressQuery);
        const progressData = progressSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          startedAt: doc.data().startedAt.toDate(),
          completedAt: doc.data().completedAt?.toDate()
        })) as UserProgress[];
        setUserProgress(progressData);
      }

      // Load training sessions
      const sessionsQuery = query(collection(db, 'trainingSessions'), orderBy('date', 'asc'));
      const sessionsSnapshot = await getDocs(sessionsQuery);
      const sessionsData = sessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date.toDate()
      })) as TrainingSession[];
      setTrainingSessions(sessionsData);

      setLoading(false);
    } catch (error) {
      console.error('Error loading training data:', error);
      setLoading(false);
    }
  };

  const createTrainingModule = async () => {
    try {
      const moduleData = {
        ...newModule,
        materials: [],
        exercises: [],
        isPublished: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        enrolledCount: 0,
        completionRate: 0,
        rating: 0
      };

      await addDoc(collection(db, 'trainingModules'), moduleData);
      setShowCreateModule(false);
      setNewModule({
        title: '',
        category: 'podcast',
        description: '',
        duration: 60,
        difficulty: 'beginner',
        instructor: '',
        prerequisites: [],
        learningObjectives: [],
        tags: []
      });
      loadData();
    } catch (error) {
      console.error('Error creating training module:', error);
    }
  };

  const createTrainingSession = async () => {
    try {
      const sessionData = {
        ...newSession,
        currentParticipants: 0,
        isActive: true,
        participants: []
      };

      await addDoc(collection(db, 'trainingSessions'), sessionData);
      setShowCreateSession(false);
      setNewSession({
        moduleId: '',
        title: '',
        instructor: '',
        date: new Date(),
        duration: 120,
        maxParticipants: 20,
        location: 'online',
        description: ''
      });
      loadData();
    } catch (error) {
      console.error('Error creating training session:', error);
    }
  };

  const enrollInModule = async (moduleId: string) => {
    try {
      const module = trainingModules.find(m => m.id === moduleId);
      if (!module || !user) return;

      const progressData = {
        userId: user.uid,
        moduleId,
        moduleTitle: module.title,
        progress: 0,
        completedSections: [],
        currentSection: 'introduction',
        startedAt: new Date(),
        exercisesCompleted: [],
        certificateEarned: false,
        notes: ''
      };

      await addDoc(collection(db, 'userProgress'), progressData);
      loadData();
    } catch (error) {
      console.error('Error enrolling in module:', error);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'podcast': return '🎙️';
      case 'presentation': return '🎤';
      case 'networking': return '🤝';
      case 'leadership': return '👑';
      case 'technical': return '💻';
      default: return '📚';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'text-green-600';
    if (progress >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Admin-only access check
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Training Center</h1>
          <p className="text-gray-600 mb-6">
            Deze functie is momenteel alleen beschikbaar voor administrators. 
            We werken aan het uitbreiden van deze functionaliteit voor alle gebruikers.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-sm">
              💡 <strong>Coming Soon:</strong> YouTube integratie, training modules, en meer!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <PageHeader 
        title="Training Center"
        subtitle="Ontwikkel je vaardigheden met professionele training modules"
        icon={<span>🎓</span>}
      />
      
      {/* Tab Navigation */}
      <TabNav 
        tabs={[
          { href: '#modules', label: 'Modules', icon: <span>📚</span>, onClick: () => setActiveTab('modules') },
          { href: '#sessions', label: 'Sessies', icon: <span>🎯</span>, onClick: () => setActiveTab('sessions') },
          { href: '#progress', label: 'Mijn Voortgang', icon: <span>📊</span>, onClick: () => setActiveTab('progress') },
          { href: '#mockups', label: 'Mockups', icon: <span>📦</span>, onClick: () => setActiveTab('mockups') },
          ...(isAdmin ? [{ href: '#create', label: 'Module Aanmaken', icon: <span>➕</span>, onClick: () => setActiveTab('create') }] : []),
        ]}
        activeTab={activeTab}
      />
      
      {/* Main Content */}
      <div className={containerClass}>
        <div className="grid gap-6">
          {/* Mockups Tab */}
          {activeTab === 'mockups' && (
            <SectionCard>
              <MockupMaker />
            </SectionCard>
          )}
          {/* Modules Tab */}
          {activeTab === 'modules' && (
            <SectionCard>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Training Modules</h2>
                <button
                  onClick={() => setShowCreateModule(true)}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-2 rounded-lg font-medium hover:from-blue-600 hover:to-purple-600"
                >
                  Module Aanmaken
                </button>
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {trainingModules.map((module) => (
                  <div key={module.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                                         <div className="flex justify-between items-start mb-4">
                       <div className="flex items-center space-x-2">
                         <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                           {module.difficulty.charAt(0).toUpperCase()}
                         </div>
                         <div>
                           <h3 className="font-semibold text-gray-900">{module.title}</h3>
                           <p className="text-sm text-gray-600">{module.category}</p>
                         </div>
                       </div>
                       <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                         module.isPublished ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                       }`}>
                         {module.isPublished ? 'Beschikbaar' : 'Concept'}
                       </span>
                     </div>

                     <p className="text-gray-600 mb-4">{module.description}</p>

                     <div className="space-y-3 mb-4">
                       <div className="grid grid-cols-2 gap-2 text-sm">
                         <div>
                           <span className="text-gray-600">Duur:</span>
                           <span className="ml-1 font-medium">{module.duration} min</span>
                         </div>
                         <div>
                           <span className="text-gray-600">Niveau:</span>
                           <span className="ml-1 font-medium">{module.difficulty}</span>
                         </div>
                         <div>
                           <span className="text-gray-600">Inschrijvingen:</span>
                           <span className="ml-1 font-medium">{module.enrolledCount}</span>
                         </div>
                         <div>
                           <span className="text-gray-600">Rating:</span>
                           <span className="ml-1 font-medium">{module.rating}/5</span>
                         </div>
                       </div>
                     </div>

                     <div className="flex space-x-2">
                       <button
                         onClick={() => {
                           setSelectedModule(module);
                         }}
                         className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 rounded-md text-sm font-medium transition-colors"
                       >
                         Details
                       </button>
                       <button
                         onClick={() => enrollInModule(module.id)}
                         className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-3 rounded-md text-sm font-medium transition-colors"
                       >
                         Inschrijven
                       </button>
                     </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* Sessions Tab */}
          {activeTab === 'sessions' && (
            <SectionCard>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">🎯 Training Sessies</h2>
                {isAdmin && (
                  <button
                    onClick={() => setShowCreateSession(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                  >
                    ➕ Nieuwe Sessie
                  </button>
                )}
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {trainingSessions.map((session) => (
                  <div key={session.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-2">{session.title}</h3>
                    <p className="text-sm text-gray-600 mb-4">{session.description}</p>
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Instructor:</span>
                        <span className="text-gray-900">{session.instructor}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Datum:</span>
                        <span className="text-gray-900">{session.date.toLocaleDateString('nl-NL')}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Deelnemers:</span>
                        <span className="text-gray-900">{session.currentParticipants}/{session.maxParticipants}</span>
                      </div>
                    </div>
                    <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium transition-colors">
                      Deelnemen
                    </button>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* Progress Tab */}
          {activeTab === 'progress' && (
            <SectionCard>
              <h2 className="text-2xl font-bold text-gray-900">📊 Mijn Voortgang</h2>
              
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {userProgress.map((progress) => (
                  <div key={progress.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-2">{progress.moduleTitle}</h3>
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Voortgang:</span>
                        <span className={`font-medium ${getProgressColor(progress.progress)}`}>
                          {progress.progress}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress.progress}%` }}
                        ></div>
                      </div>
                    </div>
                    <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium transition-colors">
                      Doorgaan
                    </button>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* Create Tab */}
          {activeTab === 'create' && isAdmin && (
            <SectionCard>
              <h2 className="text-2xl font-bold text-gray-900">⚙️ Beheer</h2>
              
              <div className="grid gap-6 md:grid-cols-2">
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Nieuwe Module</h3>
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Module titel"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <textarea
                      placeholder="Beschrijving"
                      rows={3}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium transition-colors">
                      Module Aanmaken
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Nieuwe Sessie</h3>
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Sessie titel"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <textarea
                      placeholder="Beschrijving"
                      rows={3}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium transition-colors">
                      Sessie Aanmaken
                    </button>
                  </div>
                </div>
              </div>
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainingCenter;
